import { useEntityNotes } from '../../components/entity-notes/useEntityNotes';
import { inNotesCell, notesGridSx } from '../../components/entity-notes/notesColumn';
import { useState, useMemo, useRef } from 'react';
import {
  Box, } from '@mui/material';
import TooltipIconButton from '../../components/TooltipIconButton';
import { Add, ContentCopy, Edit, Visibility, VisibilityOff } from '@mui/icons-material';
import { DataGridPremium, GridColDef, GridRenderCellParams, GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import type { GridSortModel } from '@mui/x-data-grid-premium';
import TagChips from '../../components/TagChips';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { networksService } from '../../services/networks.service';
import { useSnackbar } from 'notistack';
import EntityGridToolbar from '../../components/EntityGridToolbar';
import TagSelect from '../../components/TagSelect';
import BulkEditDialog, { BulkFieldDef } from '../../components/BulkEditDialog';
import { resolveSelectedIds, emptySelection, buildEntityWhere, applyBulkUpdate, BulkResult, actionsPinnedRight } from '../../utils/entity-list';
import { useServerPagination } from '../../hooks/useServerPagination';
import { useGridHeight } from '../../hooks/useGridHeight';
import { useUrlState, codecs } from '../../hooks/useUrlState';
import { useDebounced } from '../../hooks/useDebounced';
import ActivityCell, { activityOrderBy } from '../../components/ActivityCell';

const BULK_FIELDS: BulkFieldDef[] = [
  { key: 'tag_ids', label: 'Tags', type: 'tags' },
];

export default function NetworksListTab() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [selection, setSelection] = useState<GridRowSelectionModel>(emptySelection);
  const [search, setSearch] = useUrlState('q', '', codecs.str);
  const [filterTagIds, setFilterTagIds] = useUrlState<string[]>('tags', [], codecs.json<string[]>());
  const [statuses, setStatuses] = useUrlState<string[]>('status', ['active'], codecs.statusCsv);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const gridHeight = useGridHeight(filterRef);

  const debouncedSearch = useDebounced(search);
  const where = useMemo(
    () => buildEntityWhere('network', { search: debouncedSearch, statuses, tagIds: filterTagIds }, ['name']),
    [debouncedSearch, statuses, filterTagIds],
  );
  const { gridProps, offset, limit } = useServerPagination(50, where);
  const [sort, setSort] = useState<GridSortModel>([{ field: '_activity', sort: 'desc' }]);
  const orderBy = activityOrderBy(sort);


  const { data, isFetching } = useQuery({
    queryKey: ['networks', where, offset, limit, sort],
    queryFn: () => networksService.query({ where, offset, limit, ...(orderBy && { order_by: orderBy, order: sort[0]?.sort === 'asc' ? 'asc' as const : 'desc' as const }) }),
    placeholderData: (prev) => prev,
  });

  const rows = useMemo(() => data?.data || [], [data]);

  const selectedIds = useMemo(() => resolveSelectedIds(selection, rows), [selection, rows]);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['networks'] });
  const notes = useEntityNotes('network');
  const bulkAction = async (fn: () => Promise<unknown>, okMsg: string) => {
    try { await fn(); invalidate(); setSelection(emptySelection); enqueueSnackbar(okMsg, { variant: 'success' }); }
    catch (e) { enqueueSnackbar(e instanceof Error ? e.message : 'Action failed', { variant: 'error' }); }
  };
  const archiveSelected = () => bulkAction(() => Promise.all(selectedIds.map((id) => networksService.delete(id))), `Archived ${selectedIds.length}`);
  const unarchiveSelected = () => bulkAction(() => Promise.all(selectedIds.map((id) => networksService.unarchive(id))), `Unarchived ${selectedIds.length}`);
  const runBulk = async (result: BulkResult) => {
    setBulkLoading(true);
    try {
      await applyBulkUpdate((id, patch) => networksService.update(id, patch), selectedIds, rows, result);
      queryClient.invalidateQueries({ queryKey: ['networks'] });
      setBulkOpen(false); setSelection(emptySelection);
      enqueueSnackbar(`Updated ${selectedIds.length} ${selectedIds.length === 1 ? 'item' : 'items'}`, { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Bulk update failed', { variant: 'error' });
    } finally { setBulkLoading(false); }
  };

  const columns: GridColDef[] = [
    { field: 'ref', headerName: 'Ref', width: 90, sortable: false, valueGetter: (_v, row) => row.ref || '—' },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 180 },
    {
      field: 'click_id_param', headerName: 'Click ID Param', width: 150,
      valueFormatter: (v) => v || 'click_id',
    },
    {
      field: 'currency_code', headerName: 'Currency', width: 100,
      valueFormatter: (v) => v || 'USD',
    },
    {
      field: 'report_to_source',
      headerName: 'Report to Source',
      width: 150,
      renderCell: (params: GridRenderCellParams) => <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>{params.value ? 'Yes' : 'No'}</Box>,
    },
    {
      field: '_activity', headerName: 'Activity', width: 150, filterable: false,
      description: 'Non-bot visits in the last hour and the last 24 hours. Sorts by the last 24 hours.',
      renderCell: (p: GridRenderCellParams) => (
        <ActivityCell
          activity={p.row.activity_status} senderLabel="Offers under it"
          parts={['expected', 'h24', 'h1']} kind="network" entityId={p.row.id}
          updatedAt={p.row.activity_updated_at}
        />
      ),
    },
    notes.column,
    { field: 'tag_ids', headerName: 'Tags', flex: 1, minWidth: 150, sortable: false, renderCell: (p: GridRenderCellParams) => <TagChips ids={p.value ?? []} /> },
    {
      field: 'created_at', headerName: 'Created', width: 120,
      valueFormatter: (value) => new Date(value).toLocaleDateString(),
    },
    {
      field: 'actions', headerName: '', width: 90, sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0, alignItems: 'center', height: '100%' }}>
          <TooltipIconButton size="small" onClick={() => navigate(`/networks/${params.row.id}`)} title="Edit">
            <Edit sx={{ fontSize: 16 }} />
          </TooltipIconButton>
          <TooltipIconButton size="small" onClick={() => navigate(`/networks/${params.row.id}/duplicate`)} title="Duplicate">
            <ContentCopy sx={{ fontSize: 16 }} />
          </TooltipIconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      {/* Empty on purpose: useGridHeight measures the grid's top from here. */}
      <Box ref={filterRef} />

      <DataGridPremium
        rows={rows}
        columns={columns}
        loading={isFetching}
        {...gridProps(data?.total)}
        sortingMode="server"
        sortModel={sort}
        onSortModelChange={setSort}
        showToolbar
        slots={{ toolbar: EntityGridToolbar }}
        slotProps={{ toolbar: {
          search, onSearch: setSearch,
          filters: <TagSelect value={filterTagIds} onChange={setFilterTagIds} label="Filter by tags" allowCreate={false} sx={{ minWidth: 200 }} />,
          statuses: { value: statuses, onChange: setStatuses },
          actions: [{ icon: Add, hint: 'Add network', onClick: () => navigate('/networks/new') }],
          selectedCount: selectedIds.length,
          pluralName: 'networks',
          selectionActions: [
            { icon: Edit, hint: 'Bulk edit', onClick: () => setBulkOpen(true), color: 'inherit' as const },
            { icon: VisibilityOff, hint: 'Archive', onClick: archiveSelected, color: 'inherit' as const },
            { icon: Visibility, hint: 'Unarchive', onClick: unarchiveSelected, color: 'inherit' as const },
          ],
        } }}
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={selection}
        onRowSelectionModelChange={setSelection}
        keepNonExistentRowsSelected
        onRowDoubleClick={(params: GridRowParams, event: React.MouseEvent) => {
          const target = event.target as HTMLElement;
          if (target.closest('[data-field="actions"]')) return;
          if (notes.editable && inNotesCell(target)) return;
          navigate(`/networks/${params.row.id}`);
        }}
        {...notes.gridProps}
        initialState={actionsPinnedRight()}
        sx={{ ...notesGridSx, bgcolor: 'background.paper', borderRadius: 2, height: gridHeight }}
      />

      <BulkEditDialog
        open={bulkOpen}
        count={selectedIds.length}
        fields={BULK_FIELDS}
        loading={bulkLoading}
        onClose={() => setBulkOpen(false)}
        onApply={runBulk}
      />
    </Box>
  );
}
